import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import Reveal from "@/components/Reveal";
import { useCart } from "@/context/CartContext";
import { api } from "@/lib/api";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [result, setResult] = useState(null);
  const [failed, setFailed] = useState(false);
  const { clear } = useCart();
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setFailed(true);
      return;
    }
    let attempts = 0;
    const timer = setInterval(async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setResult(data);
          if (!cleared.current) {
            cleared.current = true;
            clear();
          }
          clearInterval(timer);
        } else if (["failed", "expired"].includes(data.status) || attempts >= 15) {
          setFailed(true);
          clearInterval(timer);
        }
      } catch {
        if (attempts >= 15) {
          setFailed(true);
          clearInterval(timer);
        }
      }
    }, 2000);
    return () => clearInterval(timer);
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <main className="min-h-screen bg-[#0B1120] flex items-center justify-center px-6 pt-24 pb-16">
      <Reveal>
        <div
          data-testid="payment-success-panel"
          className="max-w-lg w-full bg-[#0F172A] border border-white/10 p-10 text-center"
        >
          {!result && !failed && (
            <>
              <Loader2 size={52} className="text-[#ED1C24] mx-auto mb-6 animate-spin" />
              <h1 className="font-display font-semibold text-white text-3xl mb-3">
                Confirming your payment<span className="text-[#ED1C24]">...</span>
              </h1>
              <p className="text-slate-400 text-sm">
                Hold on — we're verifying the transaction with Stripe.
              </p>
            </>
          )}

          {result && (
            <>
              <CheckCircle2 size={56} className="text-[#FFC61A] mx-auto mb-6" />
              <h1 className="font-display font-semibold text-white text-3xl mb-3">
                Payment successful<span className="text-[#ED1C24]">.</span>
              </h1>
              <p className="text-slate-400 text-sm mb-2">Your order number</p>
              <p data-testid="paid-order-number" className="font-display text-2xl text-[#FFC61A] mb-6">
                {result.order_number}
              </p>
              <p className="text-slate-400 text-sm leading-relaxed mb-8">
                Your payment has been received. Our team will contact you to
                arrange delivery. Keep your order number to track the delivery.
              </p>
              <div className="flex gap-3">
                <Link
                  data-testid="payment-track-order-btn"
                  to="/tracking"
                  className="flex-1 bg-[#ED1C24] hover:bg-[#C81016] text-white font-medium py-3.5 transition-colors duration-300"
                >
                  Track Order
                </Link>
                <Link
                  data-testid="payment-back-shop-btn"
                  to="/shop"
                  className="flex-1 border border-white/15 hover:border-[#ED1C24] text-slate-300 py-3.5 text-sm transition-colors duration-300 flex items-center justify-center"
                >
                  Back to Shop
                </Link>
              </div>
            </>
          )}

          {failed && (
            <>
              <XCircle size={56} className="text-[#ED1C24] mx-auto mb-6" />
              <h1 className="font-display font-semibold text-white text-3xl mb-3">
                Payment not confirmed<span className="text-[#ED1C24]">.</span>
              </h1>
              <p className="text-slate-400 text-sm leading-relaxed mb-8">
                We couldn't confirm this payment. If money was charged, contact
                us at +670 7713 8966 with your order details.
              </p>
              <Link
                data-testid="payment-failed-shop-btn"
                to="/shop"
                className="inline-block bg-[#ED1C24] hover:bg-[#C81016] text-white font-medium px-8 py-3.5 transition-colors duration-300"
              >
                Back to Shop
              </Link>
            </>
          )}
        </div>
      </Reveal>
    </main>
  );
}
